import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { motion } from 'motion/react'
import { Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { about } from '@/constants/content'

const COLORS = ['#22ffb2', '#02afad', '#eef2ef', '#ffffff']

// Fixed spread of confetti pieces (no randomness, so server and browser always agree).
const PIECES = Array.from({ length: 22 }, (_, index) => {
    const angle = (index / 22) * Math.PI * 2 + (index % 3) * 0.21
    const distance = 90 + ((index * 37) % 70)

    return {
        x: Math.round(Math.cos(angle) * distance * 1.6),
        y: Math.round(Math.sin(angle) * distance) - 30,
        rotate: ((index * 53) % 360) - 180,
        color: COLORS[index % COLORS.length],
        round: index % 4 === 0,
        size: 7 + (index % 3) * 3
    }
})

const Confetti: FC<{ burst: number }> = ({ burst }): ReactNode => {
    if (burst === 0) return null

    return (
        <span key={burst} aria-hidden='true' className='pointer-events-none absolute left-1/2 top-1/2 z-20'>
            {PIECES.map((piece, index) => (
                <motion.span
                    key={index}
                    className={cn('absolute block', piece.round ? 'rounded-full' : 'rounded-[2px]')}
                    style={{ width: piece.size, height: piece.size * (piece.round ? 1 : 0.55), backgroundColor: piece.color, left: -piece.size / 2, top: -piece.size / 2 }}
                    initial={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 0.6 }}
                    animate={{ x: piece.x, y: [0, piece.y, piece.y + 90], rotate: piece.rotate * 3, opacity: [1, 1, 0], scale: 1 }}
                    transition={{ duration: 1.25, ease: [0.15, 0.85, 0.35, 1], times: [0, 0.55, 1] }}
                />
            ))}
        </span>
    )
}

// The World Champion moment: the title stamps down onto the card and throws confetti. Tap or hover for more.
const Champion: FC = (): ReactNode => {
    const reduce = useReduceMotion()
    const [burst, setBurst] = useState(0)
    const celebrate = () => setBurst(value => value + 1)

    return (
        <div className='relative overflow-hidden rounded-lg border-2 border-ink bg-ink p-6 text-paper shadow-[10px_10px_0_0_var(--color-blaze)] sm:p-10 lg:p-14'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -right-16 -top-16 size-72 rounded-full border-[28px] border-paper/5 sm:size-96'
            />

            <div className='relative grid gap-8 lg:grid-cols-[auto_1fr] lg:gap-12'>
                <motion.div
                    aria-hidden='true'
                    className='grid size-20 place-items-center rounded-full border-2 border-paper bg-blaze text-ink sm:size-24'
                    initial={{ rotate: -30, scale: 0.6 }}
                    whileInView={{ rotate: 0, scale: 1 }}
                    whileHover={{ rotate: [0, -12, 10, -6, 0], transition: { duration: 0.6 } }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 14 }}
                >
                    <Trophy className='size-10 sm:size-12' />
                </motion.div>

                <div>
                    <p className='font-heading text-2xl font-semibold leading-snug tracking-[-0.01em] sm:text-3xl lg:text-4xl'>
                        {about.champion.lead}

                        {' '}

                        {about.champion.claimLead}

                        {' '}

                        <span className='text-[#22FFB2]'>
                            {about.champion.claim}
                        </span>

                        {' '}

                        {about.champion.shout}

                        {' '}

                        <motion.span
                            onClick={celebrate}
                            onHoverStart={celebrate}
                            className='relative mt-4 block w-fit cursor-pointer select-none rounded-md border-2 border-paper bg-blaze px-4 py-2 font-heading text-[clamp(2.25rem,8vw,5.5rem)] font-extrabold leading-[0.95] tracking-[-0.03em] text-ink wdth-78 sm:px-6 sm:py-3'
                            initial={{ scale: 2.1, rotate: -14, opacity: 0 }}
                            whileInView={{ scale: 1, rotate: -2.5, opacity: 1 }}
                            whileTap={{ scale: 0.94 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ type: 'spring', stiffness: 420, damping: 16 }}
                            onAnimationComplete={(definition) => {
                                if (!reduce && typeof definition === 'object' && definition !== null && 'rotate' in definition && burst === 0) celebrate()
                            }}
                        >
                            {about.champion.title}
                            {!reduce && <Confetti burst={burst} />}
                        </motion.span>
                    </p>

                    <p className='mt-8 max-w-[44rem] text-lg leading-relaxed text-paper/85 sm:text-xl'>
                        {about.champion.story}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default Champion
